import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, ASSETS } from "@/data/siteContent";

export default function Navbar({ onBookConsultation }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-navy/98 backdrop-blur-md shadow-[0_10px_30px_-20px_rgba(0,0,0,0.6)]"
          : "bg-navy/85 backdrop-blur-sm"
      }`}
    >
      <div className="container-prose flex items-center justify-between py-4 md:py-5">
        <a
          href="#top"
          onClick={(e) => handleNav(e, "#top")}
          className="flex items-center gap-3 group"
          data-testid="navbar-logo-link"
        >
          <img
            src={ASSETS.logoBadge}
            alt="AceMax Consulting shield"
            className="h-10 w-10 md:h-12 md:w-12 object-contain drop-shadow-[0_2px_12px_rgba(245,197,24,0.25)]"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-white font-serif text-lg md:text-xl tracking-wide">
              AceMax
            </span>
            <span className="text-gold text-[10px] md:text-[11px] tracking-[0.3em] uppercase">
              Consulting
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleNav(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-white/90 hover:text-gold text-sm font-medium tracking-wide transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 group-hover:w-full h-px bg-gold transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex">
          <MagneticBookButton onClick={onBookConsultation} />
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-navy-dark/98 backdrop-blur-lg overflow-hidden border-t border-white/10"
          >
            <nav className="container-prose py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => handleNav(e, l.href)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="text-white/90 hover:text-gold py-3 text-base font-medium border-b border-white/5"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="mobile-book-consultation"
                onClick={() => {
                  setOpen(false);
                  onBookConsultation();
                }}
                className="mt-4 bg-gold text-navy font-semibold py-3 px-5 rounded-sm tracking-wide hover:bg-gold-hover transition-colors"
              >
                Book a Free Consultation
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

function MagneticBookButton({ onClick }) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: (e.clientX - r.left - r.width / 2) * 0.25, y: (e.clientY - r.top - r.height / 2) * 0.35 });
  };
  const onLeave = () => setPos({ x: 0, y: 0 });

  return (
    <motion.button
      data-testid="navbar-book-consultation"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      animate={{ x: pos.x, y: pos.y }}
      transition={{ type: "spring", stiffness: 180, damping: 15 }}
      className="bg-gold text-navy px-5 py-2.5 rounded-sm text-sm font-semibold tracking-wide hover:bg-gold-hover transition-colors shadow-[0_10px_30px_-12px_rgba(245,197,24,0.6)]"
    >
      Book a Consultation
    </motion.button>
  );
}
